import { useState } from "react";
import { Button } from "@/components/ui/button";
import { BadgeCheck, Loader2, MessageSquareText, ExternalLink, Heart, Repeat2, Users } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtNum } from "@/lib/format";

const ageLabel = (d) => (d == null ? null : d === 0 ? "Created today" : d < 30 ? `Created ${d} day${d === 1 ? "" : "s"} ago` : d < 365 ? `Created ${Math.round(d / 30)} mo ago` : `Created ${(d / 365).toFixed(1)} yrs ago`);
const postAgo = (iso) => { if (!iso) return ""; const h = Math.round((Date.now() - new Date(iso)) / 36e5); return h < 1 ? "just now" : h < 24 ? `${h}h` : `${Math.round(h / 24)}d`; };

function Stat({ label, value, tone = "text-slate-100", testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`font-mono text-sm ${value == null ? "text-slate-600" : tone}`}>{value ?? "N/A"}</div>
    </div>
  );
}

export function XProfileCard({ tw }) {
  const [posts, setPosts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  if (!tw?.handle) {
    if (tw?.kind === "community" && tw.url) {
      return (
        <div className="flex items-center justify-between gap-3 text-sm" data-testid="x-community">
          <a href={tw.url} target="_blank" rel="noreferrer" className="font-mono text-emerald-400 hover:underline inline-flex items-center gap-1.5"><Users className="h-4 w-4" /> X Community</a>
          <span className="font-mono text-slate-500 text-xs">no official account — community page only</span>
        </div>
      );
    }
    if (tw?.url) return <a href={tw.url} target="_blank" rel="noreferrer" className="text-sm font-mono text-emerald-400 hover:underline" data-testid="x-link">Open X link</a>;
    return <p className="text-sm font-mono text-slate-500" data-testid="x-none">No X account linked</p>;
  }
  if (tw.status !== "ok") {
    return (
      <div className="flex items-center justify-between gap-3 text-sm" data-testid="x-unavailable">
        <a href={tw.url} target="_blank" rel="noreferrer" className="font-mono text-emerald-400 hover:underline">{tw.handle}</a>
        <span className="font-mono text-slate-500">{tw.status === "none" ? "Account not found on X" : "X data unavailable"}</span>
      </div>
    );
  }

  const young = tw.ageDays != null && tw.ageDays < 30;
  const small = tw.followers != null && tw.followers < 1000;
  const showActivity = async () => {
    if (posts) { setOpen((o) => !o); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/x/activity/${encodeURIComponent(tw.handle.replace("@", ""))}`);
      setPosts(data.posts || []); setOpen(true);
    } catch (err) {
      const st = err.response?.status;
      if (st === 402) toast.error("X activity is a Pro feature", { action: { label: "Go Pro", onClick: () => (window.location.href = "/pricing") } });
      else toast.error(st === 503 ? "X data unavailable right now" : "Could not load X activity");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="x-profile">
      <div className="flex items-start gap-3">
        {tw.profilePicture ? <img src={tw.profilePicture} alt="" className="h-10 w-10 rounded-full border border-[#334566] shrink-0" /> : <div className="h-10 w-10 rounded-full bg-[#1a2333] shrink-0" />}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <a href={tw.url} target="_blank" rel="noreferrer" className="font-mono text-emerald-400 hover:underline" data-testid="x-handle">{tw.handle}</a>
            {tw.verified && <span className="inline-flex items-center gap-0.5 text-[10px] font-mono text-sky-300" data-testid="x-verified"><BadgeCheck className="h-3.5 w-3.5" /> verified</span>}
          </div>
          {tw.bio && <p className="text-xs text-slate-400 mt-0.5 line-clamp-2" data-testid="x-bio">{tw.bio}</p>}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
        <Stat label="Followers" value={tw.followers != null ? fmtNum(tw.followers) : null} tone={small ? "text-amber-300" : "text-slate-100"} testid="x-followers" />
        <Stat label="Following" value={tw.following != null ? fmtNum(tw.following) : null} testid="x-following" />
        <Stat label="Posts" value={tw.posts != null ? fmtNum(tw.posts) : null} testid="x-posts" />
        <Stat label="Account age" value={ageLabel(tw.ageDays)} tone={young ? "text-red-400" : "text-slate-100"} testid="x-age" />
      </div>
      <div className="mt-3 flex items-center gap-3 flex-wrap">
        <Button size="sm" variant="outline" asChild className="border-[#334566] bg-transparent text-slate-200 hover:bg-[#1a2333] h-8 text-xs">
          <a href={tw.url} target="_blank" rel="noreferrer" data-testid="x-open-btn"><ExternalLink className="h-3.5 w-3.5 mr-1.5" />Open on X</a>
        </Button>
        <Button size="sm" variant="outline" onClick={showActivity} disabled={loading} className="border-[#334566] bg-transparent text-slate-200 hover:bg-[#1a2333] h-8 text-xs" data-testid="x-activity-btn">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <MessageSquareText className="h-3.5 w-3.5 mr-1.5" />}
          {posts && open ? "Hide X activity" : "Show X activity"}
        </Button>
        <span className="text-[10px] font-mono text-slate-600">profile cached 24h · loads posts only when you ask</span>
      </div>
      {open && posts && (
        <ul className="mt-3 space-y-2" data-testid="x-posts-list">
          {posts.length === 0 && <li className="text-xs font-mono text-slate-500">No recent posts.</li>}
          {posts.map((p) => (
            <li key={p.id} className="rounded-lg border border-[#222F43] bg-[#0d1420] p-2.5 text-xs" data-testid={`x-post-${p.id}`}>
              <p className="text-slate-200 whitespace-pre-wrap break-words">{p.text}</p>
              <div className="flex items-center gap-3 mt-1.5 text-[10px] font-mono text-slate-500">
                <span>{postAgo(p.createdAt)}</span>
                {p.likes != null && <span className="inline-flex items-center gap-0.5"><Heart className="h-3 w-3" />{fmtNum(p.likes)}</span>}
                {p.reposts != null && <span className="inline-flex items-center gap-0.5"><Repeat2 className="h-3 w-3" />{fmtNum(p.reposts)}</span>}
                <a href={p.url} target="_blank" rel="noreferrer" className="ml-auto inline-flex items-center gap-0.5 text-emerald-400 hover:underline">open <ExternalLink className="h-3 w-3" /></a>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
